/** Codici stato documento restituiti dal backend (`codiStato`). */
export enum CodiStatoDocumentoEnum {
  ATTESA_INVIO = 'ATTESA_INVIO',
  ERRORE_INVIO = 'ERRORE_INVIO',
  ATTESA_PROTOCOLLO = 'ATTESA_PROTOCOLLO',
  ERRORE_PROTOCOLLO = 'ERRORE_PROTOCOLLO',
  DOC_PROTOCOLLATO = 'DOC_PROTOCOLLATO',
  ALL_PROTOCOLLATO = 'ALL_PROTOCOLLATO',
}

/** Descrizioni standard per `descStato` (allineate al BE). */
export const DESC_STATO_DOCUMENTO: Record<CodiStatoDocumentoEnum, string> = {
  [CodiStatoDocumentoEnum.ATTESA_INVIO]: 'Invio in corso',
  [CodiStatoDocumentoEnum.ERRORE_INVIO]: 'Errore di caricamento',
  [CodiStatoDocumentoEnum.ATTESA_PROTOCOLLO]: 'In attesa di protocollazione',
  [CodiStatoDocumentoEnum.ERRORE_PROTOCOLLO]: 'In attesa di protocollazione',
  [CodiStatoDocumentoEnum.DOC_PROTOCOLLATO]: 'Acquisito',
  [CodiStatoDocumentoEnum.ALL_PROTOCOLLATO]: 'Inviato',
};

/** Tooltip accanto al badge in `ERRORE_INVIO`. */
export const TOOLTIP_ERRORE_INVIO_DOCUMENTO =
  'Il sistema è temporaneamente non disponibile. Si prega di attendere, prima di procedere al reinvio del documento.';

/** Tooltip accanto al badge in `ERRORE_PROTOCOLLO`. */
export const TOOLTIP_ERRORE_PROTOCOLLO_DOCUMENTO =
  'Il documento è in attesa di protocollazione. Si prega di non eseguire un nuovo invio, il documento sarà protocollato appena possibile. Per eventuali scadenze, fa fede la data di invio del documento.';

export function normalizeCodiStatoDocumento(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

/** `true` se `codiStato` è uno dei codici enum (case-insensitive). */
export function isKnownCodiStatoDocumento(value: string | null | undefined): boolean {
  const codi = normalizeCodiStatoDocumento(value);
  return Object.values(CodiStatoDocumentoEnum).includes(codi as CodiStatoDocumentoEnum);
}

/** Mappa `codiStato` BE verso enum; codice non riconosciuto → `DOC_PROTOCOLLATO` (solo uso interno). */
export function mapToCodiStatoDocumento(value: string): CodiStatoDocumentoEnum {
  const codi = normalizeCodiStatoDocumento(value);
  if (Object.values(CodiStatoDocumentoEnum).includes(codi as CodiStatoDocumentoEnum)) {
    return codi as CodiStatoDocumentoEnum;
  }

  return CodiStatoDocumentoEnum.DOC_PROTOCOLLATO;
}

export function isCodiStatoDocumentoChip(value: string | null | undefined): boolean {
  const codi = normalizeCodiStatoDocumento(value);
  return (
    codi === CodiStatoDocumentoEnum.ERRORE_INVIO
    || codi === CodiStatoDocumentoEnum.ERRORE_PROTOCOLLO
    || codi === CodiStatoDocumentoEnum.ATTESA_PROTOCOLLO
  );
}

export function isCodiStatoDocumentoErrore(value: string | null | undefined): boolean {
  const codi = normalizeCodiStatoDocumento(value);
  return codi === CodiStatoDocumentoEnum.ERRORE_INVIO || codi === CodiStatoDocumentoEnum.ERRORE_PROTOCOLLO;
}

/** `true` solo per `ERRORE_INVIO` (il reinvio del file). `ERRORE_PROTOCOLLO` resta escluso. */
export function isCodiStatoDocumentoErroreInvio(value: string | null | undefined): boolean {
  return normalizeCodiStatoDocumento(value) === CodiStatoDocumentoEnum.ERRORE_INVIO;
}

/** Testo tooltip errore in base a `codiStato`. */
export function errorTooltipMessageForCodiStato(codiStato: string | null | undefined): string {
  const codi = normalizeCodiStatoDocumento(codiStato);
  if (codi === CodiStatoDocumentoEnum.ERRORE_PROTOCOLLO) {
    return TOOLTIP_ERRORE_PROTOCOLLO_DOCUMENTO;
  }
  if (codi === CodiStatoDocumentoEnum.ERRORE_INVIO) {
    return TOOLTIP_ERRORE_INVIO_DOCUMENTO;
  }
  return TOOLTIP_ERRORE_INVIO_DOCUMENTO;
}

export function isCodiStatoDocumentoInAttesa(value: string | null | undefined): boolean {
  const codi = normalizeCodiStatoDocumento(value);
  return codi === CodiStatoDocumentoEnum.ATTESA_INVIO || codi === CodiStatoDocumentoEnum.ATTESA_PROTOCOLLO;
}

/**
 * Testo badge da `codiStato`.
 * Se il codice è vuoto o non in enum → mostra il valore grezzo (senza mappa).
 * Se valido → `descStato` BE ha priorità, altrimenti `DESC_STATO_DOCUMENTO`.
 */
export function descStatoFromCodiStato(
  codiStato: string | null | undefined,
  descStato?: string | null,
): string {
  const raw = (codiStato ?? '').trim();
  if (!isKnownCodiStatoDocumento(raw)) {
    return raw;
  }
  const desc = descStato?.trim();
  if (desc) return desc;
  return DESC_STATO_DOCUMENTO[mapToCodiStatoDocumento(raw)] ?? '-';
}
